import React from 'react';
import LegendHeader from '../legendHeader';
import TabsTable from '../tabs/tabsTable';
import AccordionTable from '../accordion/accordionTable';
import { openLinkParent } from '../../../functions/functions';
import HeaderTable from '../headerTable/headerTable';

/**
 * Componente especializado para mostrar tablas de posiciones de baseball
 * Se adapta automáticamente entre tabs, accordion o vista directa según la cantidad de stages
 * Renderiza conferencias (AL/NL) con sus divisiones y estadísticas de equipos
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.dataTab1 - Datos de la tabla de posiciones de baseball
 * @param {Array} props.dataTab1.baseballStages - Array de stages/temporadas del baseball
 * @param {Array} props.dataTab1.baseballStages[].conferences - Conferencias (American/National League)
 * @param {Array} props.dataTab1.baseballStages[].conferences[].divisions - Divisiones (East, Central, West)
 * @param {Array} props.dataTab1.baseballStages[].conferences[].divisions[].teams - Equipos con estadísticas
 * @param {string} props.sport - Código del deporte para acceso dinámico a datos
 * 
 * @returns {JSX.Element} Tabla de posiciones adaptativa para baseball
 */
export default function TableBaseball({ dataTab1, version }) {
  const stages = dataTab1?.baseballStages || [];

  if (stages.length === 0 || !stages.some(stage => stage?.conferences?.length > 0)) {
    return (
      <>
        <div className="positions-table__header">
          <HeaderTable title="Posiciones" />
          <LegendHeader
            text1="División cerrada"
            text2="Comodín cerrado"
            text3="Clinched Playoff Berth"
          />
        </div>
        <div className="positions-table__sport">
          <div className="no-data">No hay datos disponibles</div>
        </div>
      </>
    );
  }

  const getLast10Record = (recordDetails) => {
    const last10 = recordDetails?.find(record => record?.name === "Last 10");
    return last10 ? `${last10.wins}-${last10.losses}` : '-';
  };

  const renderTable = (stage) => (
    <div className="table-content">
      {stage?.conferences?.map((conference, confIndex) => (
        <div key={conference?.conferenceId || `conf-${confIndex}`} className="conference-section marginB10">
          <h3 className="conference-title">{conference?.name || 'Conference'}</h3>
          {conference?.divisions?.map((division, divIndex) => (
            <div key={division?.divisionsId || `div-${divIndex}`} className="division-section">
              <table className="positions-table">
                <thead>
                  <tr>
                    <th className='division-title'>{division?.abbreviation || 'Division'}</th>
                    <th>W</th>
                    <th>L</th>
                    <th>PCT</th>
                    <th>GB</th>
                    <th>L10</th>
                    <th>STRK</th>
                  </tr>
                </thead>
                <tbody>
                  {division?.teams?.map((team, teamIndex) => {
                    const hasValidUrl = team?.url_wp?.length > 0 && version != "us";
                    return (
                      <tr key={team?.teamId || `team-${teamIndex}`}>
                        <td className="team-item" {...(hasValidUrl && { onClick: () => openLinkParent(team.url_wp, false) })}>
                          <img
                            src={team?.image || 'assets/default.png'}
                            alt={`Logo ${team?.abbreviation || 'Team'}`}
                            loading='lazy'
                            onError={(e) => e.target.src = 'assets/default.png'}
                          />
                          <span className={hasValidUrl ? "team-abbreviation item-link" : "team-abbreviation"}>{team?.abbreviation || '-'}</span>
                        </td>
                        <td className="team-item">{team?.record?.wins || '-'}</td>
                        <td className="team-item">{team?.record?.losses || '-'}</td>
                        <td className="team-item">{team?.record?.percentage || '-'}</td>
                        <td className="team-item">{team?.division?.gamesBehind || '-'}</td>
                        <td className="team-item">{getLast10Record(team?.recordDetails)}</td>
                        <td className="team-item">{team?.streaks?.[0]?.games || '-'}</td>
                      </tr>
                    );
                  }) || []}
                </tbody>
              </table>
            </div>
          )) || []}
        </div>
      )) || []}
    </div>
  );

  return (
    <>
      <div className="positions-table__header">
        <HeaderTable title="Posiciones" />
        <LegendHeader
          text1="División cerrada"
          text2="Comodín cerrado"
          text3="Clinched Playoff Berth"
        />
      </div>
      <div className="positions-table__sport">
        {stages.length === 2 ? (
          <TabsTable stages={stages} renderTable={renderTable} />
        ) : stages.length > 2 ? (
          <AccordionTable stages={stages} renderTable={renderTable} />
        ) : stages.length === 1 ? (
          renderTable(stages[0])
        ) : (
          <div className="no-data">No hay datos disponibles</div>
        )}
      </div>
    </>
  );
}